import React from "react"
import { connect } from "react-redux"
import { Route, Redirect, Switch } from "react-router-dom"
import { PrivateRoute } from "components"
import Strings from "utils/strings"
import {
	Dashboard,
	Login,
	RecoverPassword,
	Staff,
	Logs,
	Settings,
	AcceptInvite,
	Pages,
	EmailTemplates,
	PageDetail,
	EmailDetail,
	Error500,
	Businesses,
	BusinessDetail,
	ContractDetail,
	ContractTypes,
	OldOrders,
	OrderDetail,
	Routes as RoutesScreen,
	RouteDetail,
	OrderRoute,
	ExternalDeliveries,
	SectorsMap,
	GeojsonZones,
	Vehicles,
	Grids,
	Shifts,
	Orders,
	MasterData,
	LabelTemplates,
	LabelTemplateDetail,
	NotificationTemplates,
	NotificationTemplateDetail,
	OperationLinks,
	Messaging,
} from "screens"

export const offlinePages = [
	"/login",
	"/register",
	"/accept-invite",
	"/recover-password",
	"/confirm",
]

// Role groups for route gating, passed to PrivateRoute's `roles`. A route with
// no `roles` is open to every authenticated user. Keep these aligned with the
// group gates in screens/Sidebar, so a hidden menu entry and a blocked URL
// always agree.
export const SUPER_ROLES = ["sysadmin"]
export const ADMIN_ROLES = [...SUPER_ROLES, "admin"]
export const OPERATIONS_ROLES = [...ADMIN_ROLES, "operations"]

export class Routes extends React.Component<any, any> {
	componentDidUpdate() {
		const elem = document.getElementById("app_content")

		if (elem) {
			elem.scrollTop = 0
		}
	}

	isOfflineRoute(pathname: string) {
		const path = pathname.split("/")[1]

		return (
			offlinePages.indexOf(pathname) !== -1 ||
			offlinePages.indexOf(`/${path}`) !== -1
		)
	}

	getAuthenticatedHome(_user: any) {
		return "/dashboard"
	}

	renderOfflineRoutes() {
		return (
			<Switch>
				<Route exact path="/login" component={Login} />
				<Route exact path="/recover-password" component={RecoverPassword} />
				<Route
					exact
					path="/recover-password/:id/:code"
					component={RecoverPassword}
				/>
				<Route exact path="/accept-invite/:id/:code" component={AcceptInvite} />
				<Route
					exact
					path="/confirm/:email/:code"
					component={() => <h1>{Strings.authentication.confirmAccount}</h1>}
				/>
				<Route exact path="/500" component={Error500} />
				<Redirect to="/login" />
			</Switch>
		)
	}

	render() {
		const { authResolved, token, router, user } = this.props
		const pathname = router?.location?.pathname || "/"
		const defaultAuthenticatedPath = this.getAuthenticatedHome(user)
		const isOfflineRoute = this.isOfflineRoute(pathname)
		const isAuthenticated = Boolean(token) && Boolean(user)

		if (!authResolved) {
			return null
		}

		if (isOfflineRoute) {
			if (isAuthenticated && pathname !== defaultAuthenticatedPath) {
				return <Redirect to={defaultAuthenticatedPath} />
			}

			return this.renderOfflineRoutes()
		}

		return (
			<Switch>
				<PrivateRoute exact path="/dashboard" component={Dashboard} />
				<PrivateRoute exact path="/businesses" component={Businesses} />
				<PrivateRoute exact path="/businesses/:id" component={BusinessDetail} />
				<PrivateRoute
					exact
					path="/businesses/:id/contracts/:contractId"
					component={ContractDetail}
				/>
				<PrivateRoute exact path="/orders" component={Orders} />
				<PrivateRoute exact path="/orders/:id" component={OrderDetail} />
				<PrivateRoute exact path="/oldOrders" component={OldOrders} />
				<PrivateRoute exact path="/oldOrders/:id" component={OrderDetail} />
				<PrivateRoute
					exact
					path="/oldOrders/:id/route"
					component={OrderRoute}
				/>

				{/* Routes */}
				<PrivateRoute exact path="/routes" component={RoutesScreen} />
				<PrivateRoute exact path="/routes/:routeId" component={RouteDetail} />
				<PrivateRoute
					exact
					path="/external-deliveries"
					component={ExternalDeliveries}
				/>
				<PrivateRoute exact path="/sectors-map" component={SectorsMap} />

				<PrivateRoute
					exact
					path="/staff"
					roles={ADMIN_ROLES}
					component={Staff}
				/>

				{/* Operation Links */}
				<PrivateRoute
					exact
					path="/operation-links"
					roles={OPERATIONS_ROLES}
					component={OperationLinks}
				/>

				{/* Settings */}
				<PrivateRoute exact path="/settings" component={Settings} />
				<PrivateRoute exact path="/settings/pages" component={Pages} />
				<PrivateRoute exact path="/settings/pages/:id" component={PageDetail} />
				<PrivateRoute
					exact
					path="/settings/email-templates"
					component={EmailTemplates}
				/>
				<PrivateRoute
					exact
					path="/settings/email-templates/:id"
					component={EmailDetail}
				/>
				<PrivateRoute exact path="/settings/vehicles" component={Vehicles} />
				<PrivateRoute exact path="/settings/grids" component={Grids} />
				<PrivateRoute exact path="/settings/shifts" component={Shifts} />
				<PrivateRoute
					exact
					path="/settings/contract-types"
					component={ContractTypes}
				/>
				<PrivateRoute
					exact
					path="/settings/master-data"
					component={MasterData}
				/>
				<PrivateRoute
					exact
					path="/settings/label-templates"
					component={LabelTemplates}
				/>
				<PrivateRoute
					exact
					path="/settings/label-templates/:id"
					component={LabelTemplateDetail}
				/>
				<PrivateRoute
					exact
					path="/settings/notification-templates"
					component={NotificationTemplates}
				/>
				<PrivateRoute
					exact
					path="/settings/notification-templates/:id"
					component={NotificationTemplateDetail}
				/>
				<PrivateRoute
					exact
					path="/settings/geojson-zones"
					roles={ADMIN_ROLES}
					component={GeojsonZones}
				/>

				{/* Tenants route hidden until tenant development resumes.
				<PrivateRoute
					exact
					path="/tenants"
					roles={SUPER_ROLES}
					component={Tenants}
				/>
				*/}
				{/* Messaging */}
				<PrivateRoute exact path="/messaging" component={Messaging} />

				<PrivateRoute exact path="/logs" component={Logs} />
				<PrivateRoute exact path="/500" component={Error500} />
				<Redirect to="/dashboard" />
			</Switch>
		)
	}
}

const mapStateToProps = (state: any) => ({
	router: state.router,
	user: state.user,
	token: state.token,
})

export default connect(mapStateToProps)(Routes)
